import { Leaf, Recycle, Sprout, TreePine } from "lucide-react";
import { IDS } from "@/constants/testIds";
import Reveal from "@/components/site/Reveal";

export default function Sustainability() {
  return (
    <section
      id="sustainability"
      data-testid={IDS.sustainabilitySection}
      className="relative py-24 sm:py-32 bg-forest overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 -left-32 h-[26rem] w-[26rem] rounded-full bg-brand/25 blur-3xl" />
      <div className="absolute -bottom-40 -right-24 h-[24rem] w-[24rem] rounded-full bg-sage/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image — clipped spotlight frame */}
          <Reveal className="relative">
            <div className="absolute -inset-3 rounded-[2.4rem] border border-sage/15 -rotate-1" />
            <div className="relative rounded-[2rem] overflow-hidden aspect-square ring-1 ring-white/10 shadow-2xl shadow-black/40">
              <img
                src="https://customer-assets.emergentagent.com/job_sustainable-pack-8/artifacts/ntqqbgvp_90a83fd1-4140-4cb0-b783-b648233433d6.png"
                alt="100% recyclable and biodegradable packaging — Ashrita Overseas product family"
                className="h-full w-full object-cover hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.18),transparent_55%)]" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-forest/50 via-transparent to-transparent" />
            </div>
          </Reveal>

          {/* Copy */}
          <Reveal delay={0.1}>
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sage">
              Sustainability
            </span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold leading-[1.08] tracking-tight text-white">
              Committed to{" "}
              <span className="font-serif-accent italic font-medium text-sage">
                Sustainable Packaging.
              </span>
            </h2>
            <p className="mt-6 text-sage/80 text-base sm:text-lg leading-relaxed max-w-xl">
              We promote environmentally responsible packaging by connecting
              global buyers with trusted Indian manufacturers producing
              recyclable, compostable and eco-friendly packaging products — from
              paper cups to compostable food packaging and beyond.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Recycle, t: "Recyclable Materials", d: "Paperboard, kraft and natural fibres." },
                { icon: Sprout, t: "Biodegradable Options", d: "Compostable & PLA-lined alternatives." },
                { icon: TreePine, t: "Responsible Sourcing", d: "Preference for FSC-certified pulp." },
                { icon: Leaf, t: "Reduced Plastic", d: "Actively replacing single-use plastics." },
              ].map((x) => (
                <div
                  key={x.t}
                  className="flex items-start gap-3 rounded-2xl bg-white/5 p-5 border border-white/10 hover:border-sage/40 hover:bg-white/10 transition-colors duration-500"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sage/15 text-sage shrink-0">
                    <x.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-sm text-white">{x.t}</div>
                    <div className="text-xs text-sage/70 mt-0.5">{x.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
